import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Plus, Trash2 } from 'lucide-react';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { LoadingAnimation } from 'components/ui/loading-animation';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { useToast } from 'components/ui/use-toast';
import { adminContent } from 'services/api';
import type { CohortRole, CohortSummary } from 'types';
import type {
  AdminUserCohortMembership,
  AdminUserCohortMembershipWriteResponse,
  AdminUserDetail,
} from './types';

const KEEP_CURRENT_VALUE = 'keep';

const roleOptions: Array<{ value: CohortRole; label: string }> = [
  { value: 'mentee', label: 'Scholar' },
  { value: 'mentor', label: 'Mentor' },
  { value: 'cohort_director', label: 'Cohort Director' },
  { value: 'cohort_admin', label: 'Cohort Admin' },
];

const roleLabel = (role: CohortRole) => roleOptions.find((option) => option.value === role)?.label ?? role;

const normalizeList = <T,>(payload: any): T[] => {
  if (Array.isArray(payload)) return payload as T[];
  if (payload?.results && Array.isArray(payload.results)) return payload.results as T[];
  return [];
};

type CohortMembershipsPanelProps = {
  user: AdminUserDetail;
  memberships: AdminUserCohortMembership[];
  onMembershipsChange: (memberships: AdminUserCohortMembership[]) => void;
};

export const CohortMembershipsPanel: React.FC<CohortMembershipsPanelProps> = ({
  user,
  memberships,
  onMembershipsChange,
}) => {
  const { addToast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cohorts, setCohorts] = useState<CohortSummary[]>([]);
  const [cohortsLoading, setCohortsLoading] = useState(false);
  const [cohortSearch, setCohortSearch] = useState('');
  const [selectedCohortIds, setSelectedCohortIds] = useState<string[]>([]);
  const [selectedRole, setSelectedRole] = useState<CohortRole>('mentee');
  const [currentChoice, setCurrentChoice] = useState<string>(KEEP_CURRENT_VALUE);
  const [saving, setSaving] = useState(false);
  const [updatingMembershipId, setUpdatingMembershipId] = useState<string | null>(null);

  const existingCohortIds = useMemo(
    () => new Set(memberships.map((membership) => membership.cohort.id)),
    [memberships],
  );

  const currentMembership = useMemo(
    () => memberships.find((membership) => membership.is_current) ?? null,
    [memberships],
  );

  const filteredCohorts = useMemo(() => {
    const query = cohortSearch.trim().toLowerCase();
    return cohorts
      .filter((cohort) => !existingCohortIds.has(cohort.id))
      .filter((cohort) => {
        if (!query) return true;
        return (
          cohort.name.toLowerCase().includes(query) ||
          (cohort.organization?.name ?? '').toLowerCase().includes(query)
        );
      });
  }, [cohortSearch, cohorts, existingCohortIds]);

  const selectedCohorts = useMemo(
    () => cohorts.filter((cohort) => selectedCohortIds.includes(cohort.id)),
    [cohorts, selectedCohortIds],
  );

  const loadCohorts = useCallback(async () => {
    setCohortsLoading(true);
    try {
      const response = await adminContent.cohorts.list();
      setCohorts(normalizeList<CohortSummary>(response.data));
    } catch (error) {
      console.error('Failed to load cohorts', error);
      addToast({
        title: 'Error',
        description: 'Unable to load cohorts.',
        variant: 'destructive',
      });
    } finally {
      setCohortsLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    if (!dialogOpen || cohorts.length > 0 || cohortsLoading) return;
    void loadCohorts();
  }, [cohorts.length, cohortsLoading, dialogOpen, loadCohorts]);

  useEffect(() => {
    if (currentChoice === KEEP_CURRENT_VALUE) return;
    if (!selectedCohortIds.includes(currentChoice)) {
      setCurrentChoice(KEEP_CURRENT_VALUE);
    }
  }, [currentChoice, selectedCohortIds]);

  const resetDialog = useCallback(() => {
    setCohortSearch('');
    setSelectedCohortIds([]);
    setSelectedRole('mentee');
    setCurrentChoice(KEEP_CURRENT_VALUE);
  }, []);

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      setDialogOpen(open);
      if (!open) resetDialog();
    },
    [resetDialog],
  );

  const toggleSelectedCohort = useCallback((cohortId: string) => {
    setSelectedCohortIds((previous) =>
      previous.includes(cohortId)
        ? previous.filter((id) => id !== cohortId)
        : [...previous, cohortId],
    );
  }, []);

  const handleAddMemberships = useCallback(async () => {
    if (selectedCohortIds.length === 0) {
      addToast({
        title: 'Select cohorts',
        description: 'Choose at least one cohort.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const response = await adminContent.users.addCohortMemberships(user.id, {
        cohort_ids: selectedCohortIds,
        role: selectedRole,
        current_cohort_id: currentChoice === KEEP_CURRENT_VALUE ? null : currentChoice,
      });
      const result = response.data as AdminUserCohortMembershipWriteResponse;
      onMembershipsChange(result.memberships);
      setDialogOpen(false);
      resetDialog();
      addToast({
        title: 'Cohort memberships updated',
        description: `${result.created} added, ${result.updated} updated, ${result.already_present.length} already present, ${result.skipped.length} skipped.`,
      });
    } catch (error: any) {
      console.error('Failed to add cohort memberships', error);
      addToast({
        title: 'Unable to add memberships',
        description: error?.response?.data?.detail || 'The selected cohorts could not be added.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }, [
    addToast,
    currentChoice,
    onMembershipsChange,
    resetDialog,
    selectedCohortIds,
    selectedRole,
    user.id,
  ]);

  const refreshMemberships = useCallback(async () => {
    const response = await adminContent.users.listCohortMemberships(user.id);
    onMembershipsChange(response.data);
  }, [onMembershipsChange, user.id]);

  const handleRoleChange = useCallback(
    async (membership: AdminUserCohortMembership, role: CohortRole) => {
      setUpdatingMembershipId(membership.id);
      try {
        const response = await adminContent.users.updateCohortMembership(user.id, membership.id, { role });
        onMembershipsChange(
          memberships.map((item) => (item.id === membership.id ? response.data : item)),
        );
      } catch (error) {
        console.error('Failed to update cohort membership role', error);
        addToast({
          title: 'Error',
          description: 'Unable to update membership role.',
          variant: 'destructive',
        });
      } finally {
        setUpdatingMembershipId(null);
      }
    },
    [addToast, memberships, onMembershipsChange, user.id],
  );

  const handleMakeCurrent = useCallback(
    async (membership: AdminUserCohortMembership) => {
      setUpdatingMembershipId(membership.id);
      try {
        await adminContent.users.updateCohortMembership(user.id, membership.id, { is_current: true });
        await refreshMemberships();
        addToast({ title: 'Current cohort updated' });
      } catch (error) {
        console.error('Failed to update current cohort', error);
        addToast({
          title: 'Error',
          description: 'Unable to update current cohort.',
          variant: 'destructive',
        });
      } finally {
        setUpdatingMembershipId(null);
      }
    },
    [addToast, refreshMemberships, user.id],
  );

  const handleRemove = useCallback(
    async (membership: AdminUserCohortMembership) => {
      setUpdatingMembershipId(membership.id);
      try {
        await adminContent.users.removeCohortMembership(user.id, membership.id);
        await refreshMemberships();
        addToast({ title: 'Cohort membership removed' });
      } catch (error) {
        console.error('Failed to remove cohort membership', error);
        addToast({
          title: 'Error',
          description: 'Unable to remove cohort membership.',
          variant: 'destructive',
        });
      } finally {
        setUpdatingMembershipId(null);
      }
    },
    [addToast, refreshMemberships, user.id],
  );

  return (
    <section className="space-y-4 rounded-lg border border-border/40 bg-background/60 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Cohort Memberships</h2>
          <div className="text-sm text-muted-foreground">
            Current: {currentMembership?.cohort.name ?? 'None'}
          </div>
        </div>
        <Button type="button" size="auto" variant="secondary" onClick={() => setDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Add to Cohorts
        </Button>
      </div>

      {memberships.length === 0 ? (
        <div className="rounded-md border border-border/40 px-4 py-6 text-center text-sm text-muted-foreground">
          No cohort memberships.
        </div>
      ) : (
        <div className="space-y-2">
          {memberships.map((membership) => (
            <div
              key={membership.id}
              className="flex flex-col gap-3 rounded-md border border-border/40 bg-background px-3 py-3 lg:flex-row lg:items-center lg:justify-between"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-foreground">{membership.cohort.name}</span>
                  {membership.is_current ? (
                    <Badge variant="secondary" className="gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Current
                    </Badge>
                  ) : null}
                  {!membership.cohort.is_active ? <Badge variant="outline">Inactive</Badge> : null}
                </div>
                <div className="text-xs text-muted-foreground">
                  {membership.cohort.organization?.name ?? 'No organization'}
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Select
                  value={membership.role}
                  onValueChange={(value) => handleRoleChange(membership, value as CohortRole)}
                  disabled={updatingMembershipId === membership.id}
                >
                  <SelectTrigger className="w-full sm:w-[12rem]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roleOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {!membership.is_current ? (
                  <Button
                    type="button"
                    size="auto"
                    variant="secondary"
                    onClick={() => handleMakeCurrent(membership)}
                    disabled={updatingMembershipId === membership.id}
                  >
                    Make Current
                  </Button>
                ) : null}

                <Button
                  type="button"
                  size="auto"
                  variant="secondary"
                  onClick={() => handleRemove(membership)}
                  disabled={updatingMembershipId === membership.id}
                >
                  <Trash2 className="h-4 w-4" />
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Add to Cohorts</DialogTitle>
            <DialogDescription>
              Choose one or more cohorts, assign the user&apos;s role, and optionally make one selected cohort current.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="cohort-membership-role">Role</Label>
                <Select value={selectedRole} onValueChange={(value) => setSelectedRole(value as CohortRole)}>
                  <SelectTrigger id="cohort-membership-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roleOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="cohort-membership-current">Current cohort</Label>
                <Select value={currentChoice} onValueChange={setCurrentChoice}>
                  <SelectTrigger id="cohort-membership-current">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={KEEP_CURRENT_VALUE}>Keep current</SelectItem>
                    {selectedCohorts.map((cohort) => (
                      <SelectItem key={cohort.id} value={cohort.id}>
                        {cohort.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cohort-membership-search">Search cohorts</Label>
              <Input
                id="cohort-membership-search"
                value={cohortSearch}
                onChange={(event) => setCohortSearch(event.target.value)}
                placeholder="Search by cohort or organization"
              />
            </div>

            <div className="max-h-[20rem] overflow-y-auto rounded-md border border-border/40">
              {cohortsLoading ? (
                <div className="flex min-h-[10rem] items-center justify-center">
                  <LoadingAnimation />
                </div>
              ) : filteredCohorts.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No cohorts available.
                </div>
              ) : (
                filteredCohorts.map((cohort) => {
                  const checked = selectedCohortIds.includes(cohort.id);
                  return (
                    <label
                      key={cohort.id}
                      className="flex cursor-pointer items-start gap-3 border-b border-border/30 px-4 py-3 last:border-b-0 hover:bg-muted/20"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleSelectedCohort(cohort.id)}
                        aria-label={`Select ${cohort.name}`}
                      />
                      <span className="min-w-0">
                        <span className="block font-medium text-foreground">{cohort.name}</span>
                        <span className="block text-xs text-muted-foreground">
                          {cohort.organization?.name ?? 'No organization'} | {cohort.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </span>
                    </label>
                  );
                })
              )}
            </div>

            <div className="text-sm text-muted-foreground">
              {selectedCohortIds.length} selected | Role: {roleLabel(selectedRole)}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" size="auto" onClick={() => handleDialogOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" size="auto" onClick={handleAddMemberships} disabled={saving || selectedCohortIds.length === 0}>
              {saving ? 'Adding...' : 'Add Memberships'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};
